"use client";

import { useState } from "react";

interface GalleryGridProps {
  data: Record<number, string[]>;
}

export function GalleryGrid({ data }: GalleryGridProps) {
  const years = Object.keys(data).map(Number).sort((a, b) => b - a);
  const [activeYear, setActiveYear] = useState<number | "all">("all");

  const images = activeYear === "all"
    ? years.flatMap((y) => data[y].map((src) => ({ src, year: y })))
    : data[activeYear]?.map((src) => ({ src, year: activeYear })) ?? [];

  return (
    <>
      {/* Year filter pills */}
      <div className="flex flex-wrap gap-2 justify-center mb-8">
        <button
          onClick={() => setActiveYear("all")}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
            activeYear === "all"
              ? "bg-liberty-blue text-white"
              : "bg-liberty-blue/10 text-liberty-blue hover:bg-liberty-blue/20"
          }`}
        >
          All Years
        </button>
        {years.map((year) => (
          <button
            key={year}
            onClick={() => setActiveYear(year)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
              activeYear === year
                ? "bg-liberty-blue text-white"
                : "bg-liberty-blue/10 text-liberty-blue hover:bg-liberty-blue/20"
            }`}
          >
            {year}
          </button>
        ))}
      </div>

      {/* Image grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {images.map(({ src, year }, i) => (
          <div key={`${src}-${i}`} className="gallery-item aspect-square relative group">
            <img
              src={src}
              alt={`Pocket Parade ${year}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-bunting-navy/0 group-hover:bg-bunting-navy/30 transition-colors flex items-end justify-between p-2">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-bunting-navy/70 text-white text-xs px-2 py-1 rounded">
                {year}
              </span>
              <a
                href={src}
                download={`pocket-parade-${year}-${i + 1}.jpg`}
                onClick={(e) => e.stopPropagation()}
                className="opacity-0 group-hover:opacity-100 transition-opacity bg-bunting-navy/70 text-white p-1.5 rounded hover:bg-bunting-navy/90"
                title="Download photo"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" x2="12" y1="15" y2="3" />
                </svg>
              </a>
            </div>
          </div>
        ))}
      </div>

      <p className="text-center text-sm text-bunting-navy/40 mt-6">
        {images.length} photos
      </p>
    </>
  );
}
